import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Wand2, Loader2, Copy, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { api, errMsg, streamPost } from "@/lib/api";
import { POLISH_PRESETS, parseSections, emptyNote } from "@/lib/soap";
import { copyText } from "@/lib/clipboard";
import { VoiceDictation } from "@/components/VoiceDictation";

const toDisplay = (raw) => raw.replace(/^[ \t]*###[ \t]*(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN)[ \t]*:?/gim, (_, h) => h.toUpperCase()).trim();
const toSections = (txt) => parseSections(txt.replace(/^[ \t]*(?:###[ \t]*)?(SUBJECTIVE|OBJECTIVE|ASSESSMENT|PLAN)[ \t]*:?[ \t]*$/gim, "### $1"));

export default function PolishPage() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [preset, setPreset] = useState("clinical");
  const [custom, setCustom] = useState("");
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!text.trim()) return toast.error("Paste or dictate a note first");
    setBusy(true);
    setOut("");
    try {
      const full = await streamPost("/ai/polish", { note_text: text, preset, custom_instruction: custom }, (t) => setOut(toDisplay(t)));
      setOut(toDisplay(full));
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const saveAsNote = async () => {
    try {
      const sections = toSections(out);
      const hasSections = Object.values(sections).some((v) => v.trim());
      const { data } = await api.post("/notes", { ...emptyNote(), ...(hasSections ? sections : { subjective: out }), session_details: text });
      toast.success("Saved as a new note");
      navigate(`/notes/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6 fade-up">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Polish a note</h1>
        <p className="text-slate-500 mt-1">Paste a note you already wrote. Get it back clean, objective and in SOAP format.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">Your original note</h3>
              <VoiceDictation testId="polish-dictate" onText={(t) => setText((x) => (x ? `${x}\n${t}` : t))} />
            </div>
            <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Paste your session note here…" className="min-h-[280px] text-[15px] leading-relaxed" data-testid="polish-input-textarea" />
          </div>
          <div className="rounded-xl border bg-white p-5 space-y-4">
            <h3 className="font-semibold">How should it be improved?</h3>
            <div className="flex flex-wrap gap-2">
              {POLISH_PRESETS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPreset(p.key)}
                  data-testid={`polish-chip-${p.key}`}
                  className={`px-3.5 py-1.5 rounded-full text-sm border transition-colors ${preset === p.key ? "bg-primary text-white border-primary" : "bg-white hover:bg-secondary text-slate-700"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Anything else? e.g. mention caregiver training (optional)" data-testid="polish-custom-input" />
            <Button className="w-full rounded-full h-11" onClick={run} disabled={busy} data-testid="polish-run-button">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wand2 className="h-4 w-4 mr-2" />}Polish note
            </Button>
          </div>
        </div>
        <div className="space-y-4">
          {out !== null ? (
            <div className="rounded-xl border bg-white p-5 space-y-4" data-testid="polish-result-card">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="font-semibold flex items-center gap-2">
                  Polished note {busy && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
                </h3>
                <div className="flex gap-2">
                  <Button variant="outline" className="rounded-full" disabled={busy || !out.trim()} onClick={() => copyText(out, "Note copied — ready to paste")} data-testid="polish-copy-button"><Copy className="h-4 w-4 mr-2" />Copy</Button>
                  <Button className="rounded-full" disabled={busy || !out.trim()} onClick={saveAsNote} data-testid="polish-save-button"><Save className="h-4 w-4 mr-2" />Save as note</Button>
                </div>
              </div>
              <Textarea
                value={out}
                onChange={(e) => setOut(e.target.value)}
                readOnly={busy}
                placeholder="Polishing your note…"
                className="min-h-[560px] text-[15px] leading-relaxed"
                data-testid="polish-output-textarea"
              />
            </div>
          ) : (
            <div className="h-full min-h-[300px] rounded-xl border border-dashed bg-white/60 grid place-items-center text-center p-10" data-testid="polish-empty-state">
              <div>
                <Wand2 className="h-8 w-8 mx-auto text-amber-500" />
                <p className="mt-3 text-sm text-slate-500 max-w-xs">Your polished SOAP note will appear here. All your facts and data stay — nothing gets made up.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
